// La tarjeta de un documento en el chat (04-11-2026; propuesta-2/fuentes/pdf.md §3, maqueta archivo-despues.png): como un
// enlace enriquecido de Mensajes. Un PDF llega con su portada real (la página 1, dibujada por el servidor), su TÍTULO
// —no «plan-oficina.pdf»— y «PDF · 11 páginas · 2,2 MB» en gris; un informe, con su título y su entradilla; cualquier
// otro documento, con su icono. Toda la tarjeta es un solo botón y abre la hoja de vista previa.
//
// Sin oro: el oro de la oficina significa «actúa» (D-037) y abrir un documento no es actuar (antes el informe llevaba
// un borde dorado y la ficha del PDF el gris-oro de la burbuja de Pau). Hover/foco: la franja se aclara, la portada
// brilla un poco y el anillo de foco es blanco.
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { portada as portadaInforme } from '../informe/bloques';
import { claseDe, detalleDe, etiquetaDe, tituloDe, type DatosArchivo } from './presentacion';
import { urlPortada } from './url';
import { elementoDe, useVistaPrevia, type Autor } from './vistaPrevia';

/** La página 1 de un PDF (o el icono si el servidor no la pudo dibujar: nunca una imagen rota). */
export function Portada({ agentId, d, className, alFallar }: { agentId: string; d: DatosArchivo; className?: string; alFallar?: () => void }) {
  return <img src={urlPortada(agentId, d.id)} alt="" loading="lazy" decoding="async" onError={alFallar} className={className} />;
}

/** El icono de un documento sin portada: 40 px, con su extensión debajo. */
export function IconoArchivo({ d, className }: { d: DatosArchivo; className?: string }) {
  return (
    <span className={cn('flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-o-s bg-oficina-elevada text-oficina-texto2', className)} aria-hidden>
      <FileText className="h-4 w-4" />
      <span className="mt-0.5 text-o-mini font-semibold leading-none">{etiquetaDe(d).slice(0, 4)}</span>
    </span>
  );
}

export function TarjetaArchivo({ agentId, d, autor, en, hora }: { agentId: string; d: DatosArchivo; autor?: Autor; en?: string; hora?: string }) {
  const abrir = useVistaPrevia((s) => s.abrir);
  const [sinPortada, setSinPortada] = useState(false);
  const clase = claseDe(d);
  const titulo = tituloDe(d);
  const detalle = detalleDe(d);
  const entradilla = clase === 'informe' ? portadaInforme(d.resumen ?? '').entradilla : null;
  const conPortada = clase === 'pdf' && !sinPortada;
  // Una página vertical se enseña a 256 px de ancho y como mucho 320 de alto, recortada por abajo (se ve su cabecera).
  const vertical = conPortada && (d.proporcion ?? 1.4) < 1;
  return (
    <button
      type="button"
      data-testid="tarjeta-archivo"
      data-clase={clase}
      onClick={() => abrir({ elementos: [elementoDe(agentId, d)], indice: 0, ...(autor ? { autor } : {}), ...(en ? { en } : {}) })}
      aria-label={t('oficinaV2.archivo.abrir', { titulo, detalle })}
      className={cn(
        'group block max-w-full cursor-pointer overflow-hidden rounded-o-l bg-oficina-control text-left outline-none',
        'focus-visible:ring-2 focus-visible:ring-oficina-texto',
        conPortada ? (vertical ? 'w-64' : 'w-96') : 'w-96',
      )}
    >
      {conPortada ? (
        <span
          className={cn('block overflow-hidden border-b border-oficina-linea bg-oficina-elevada', vertical && 'max-h-80')}
          style={{ aspectRatio: String(d.proporcion ?? 1.4) }}
        >
          <Portada
            agentId={agentId}
            d={d}
            alFallar={() => setSinPortada(true)}
            className="block h-full w-full object-cover object-top transition-[filter] duration-150 ease-o-apple group-hover:brightness-110 group-focus-visible:brightness-110"
          />
        </span>
      ) : null}
      <span className={cn('flex gap-3 px-3.5 py-2.5 transition-colors duration-150 ease-o-apple group-hover:bg-oficina-pulsado group-focus-visible:bg-oficina-pulsado', !conPortada && clase !== 'informe' && 'items-center')}>
        {!conPortada && clase !== 'informe' ? <IconoArchivo d={d} /> : null}
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2 text-o-cuerpo font-semibold text-oficina-texto">{titulo}</span>
          {entradilla ? <span className="mt-0.5 line-clamp-2 text-o-texto text-oficina-texto2">{entradilla}</span> : null}
          <span className="mt-0.5 flex items-baseline gap-2 text-o-nota text-oficina-texto3">
            <span className="min-w-0 truncate">{detalle}</span>
            {hora ? <span className="ml-auto shrink-0 tabular-nums">{hora}</span> : null}
          </span>
        </span>
      </span>
    </button>
  );
}
